import apiClient from './apiClient'
import { sha256HexFromFile, uploadApkToPresignedUrl } from '../utils/appReleaseUpload'

// Auth Services
export const authService = {
  googleLogin: async (token) => {
    const response = await apiClient.post('/admin/auth/google-login', { token })
    return response.data
  },

  verifyAdmin: async () => {
    const response = await apiClient.get('/admin/auth/verify')
    return response.data
  },

  passwordLogin: async (email, password) => {
    const response = await apiClient.post('/admin/auth/login', { email, password })
    return response.data
  },

  forgotPassword: async (email) => {
    const response = await apiClient.post('/admin/auth/forgot-password', { email })
    return response.data
  },

  validateInvite: async (token) => {
    const response = await apiClient.post('/admin/auth/invite/validate', { token })
    return response.data
  },

  acceptInvite: async (token, password) => {
    const response = await apiClient.post('/admin/auth/accept-invite', {
      token,
      password,
    })
    return response.data
  },

  validateReset: async (token) => {
    const response = await apiClient.post('/admin/auth/reset-password/validate', {
      token,
    })
    return response.data
  },

  resetPassword: async (token, newPassword) => {
    const response = await apiClient.post('/admin/auth/reset-password', {
      token,
      newPassword,
    })
    return response.data
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.post('/admin/auth/change-password', {
      currentPassword,
      newPassword,
    })
    return response.data
  },

  provisionInvite: async ({ email, name, roleCodes }) => {
    const response = await apiClient.post('/admin/users/invite', {
      email,
      name,
      roleCodes,
    })
    return response.data
  },

  resendInvite: async (googleUserId) => {
    const response = await apiClient.post(
      `/admin/users/${googleUserId}/resend-invite`,
    )
    return response.data
  },

  deactivateUser: async (googleUserId) => {
    const response = await apiClient.post(
      `/admin/users/${googleUserId}/deactivate`,
    )
    return response.data
  },

  reactivateUser: async (googleUserId) => {
    const response = await apiClient.post(
      `/admin/users/${googleUserId}/reactivate`,
    )
    return response.data
  },

  /**
   * SUPERUSER only. Returns plaintext password once — show in a one-time dialog.
   */
  generateRandomPassword: async (googleUserId) => {
    const response = await apiClient.post(
      `/admin/users/${googleUserId}/password/generate`,
    )
    return response.data
  },
}

export const adminWhitelistService = {
  getWhitelist: async (params) => {
    const response = await apiClient.get('/admin/whitelist', { params })
    return response.data
  },
  addToWhitelist: async (email, name) => {
    const response = await apiClient.post('/admin/whitelist', { email, name })
    return response.data
  },
  removeFromWhitelist: async (email) => {
    const response = await apiClient.post('/admin/whitelist/remove', { email })
    return response.data
  },
}

export const appUserWhitelistService = {
  list: async (params) => {
    const response = await apiClient.get('/admin/app-user-whitelist', { params })
    return response.data
  },
  add: async (phone_number) => {
    const response = await apiClient.post('/admin/app-user-whitelist', {
      phone_number,
    })
    return response.data
  },
  setActive: async (id, is_active) => {
    const response = await apiClient.patch(`/admin/app-user-whitelist/${id}`, {
      is_active,
    })
    return response.data
  },
  remove: async (id) => {
    const response = await apiClient.delete(`/admin/app-user-whitelist/${id}`)
    return response.data
  },
}

export const appUserService = {
  list: async (params) => {
    const response = await apiClient.get('/admin/app-users', { params })
    return response.data
  },
  setPin: async (userId, pin) => {
    const response = await apiClient.post(`/admin/app-users/${userId}/pin`, {
      pin,
    })
    return response.data
  },
  /**
   * Returns { pin } — plaintext visible ONCE. Caller must display in a
   * show-once dialog and wipe from React state when the dialog closes.
   */
  generatePin: async (userId) => {
    const response = await apiClient.post(
      `/admin/app-users/${userId}/pin/generate`,
    )
    return response.data
  },
}

export const shopService = {
  list: async (params) => {
    const response = await apiClient.get('/admin/shops', { params })
    return response.data
  },
  get: async (orgId) => {
    const response = await apiClient.get(`/admin/shops/${orgId}`)
    return response.data
  },
  regenerateAppLockPin: async (orgId) => {
    const response = await apiClient.post(
      `/admin/shops/${orgId}/app-lock/regenerate-pin`,
    )
    return response.data
  },
  getBalance: async (orgId) => {
    const response = await apiClient.get(`/admin/shops/${orgId}/balance`)
    return response.data
  },
  update: async (orgId, data) => {
    const response = await apiClient.patch(`/admin/shops/${orgId}`, data)
    return response.data
  },
  approveRequest: async (requestId, data) => {
    const response = await apiClient.post(
      `/admin/shop-account-requests/${requestId}/approve`,
      data ?? {},
    )
    return response.data
  },
  rejectRequest: async (requestId, reason) => {
    const response = await apiClient.post(
      `/admin/shop-account-requests/${requestId}/reject`,
      { reason },
    )
    return response.data
  },
  createShop: async (data) => {
    const response = await apiClient.post('/admin/shops', data)
    return response.data
  },
  addLocationMappings: async (orgId, locationIds) => {
    const response = await apiClient.post(
      `/admin/shops/${orgId}/location-mappings`,
      { location_ids: locationIds },
    )
    return response.data
  },
  closeShop: async (orgId, data) => {
    const response = await apiClient.post(`/admin/shops/${orgId}/close`, data)
    return response.data
  },
  reopenShop: async (orgId, data) => {
    const response = await apiClient.post(`/admin/shops/${orgId}/reopen`, data)
    return response.data
  },
  uploadDocument: async (orgId, docType, file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post(
      `/admin/shops/${orgId}/documents/${docType}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
    return response.data
  },
  createExport: async (payload) => {
    const response = await apiClient.post('/admin/shops/export', payload)
    return response.data
  },
  getExportJobs: async (params) => {
    const response = await apiClient.get('/admin/shops/export-jobs', { params })
    return response.data
  },
  getExportJobUrl: async (id) => {
    const response = await apiClient.get(`/admin/shops/export-jobs/${id}/download`)
    return response.data
  },
}

/** Super-admin only: desktop web login promo chips */
export const desktopLoginChipsService = {
  get: async () => {
    const response = await apiClient.get('/admin/settings/desktop-login-chips')
    return response.data
  },
  update: async (chips) => {
    const response = await apiClient.put('/admin/settings/desktop-login-chips', chips)
    return response.data
  },
  reset: async () => {
    const response = await apiClient.post('/admin/settings/desktop-login-chips/reset')
    return response.data
  },
}

/** Super-admin only: vendor catalog (vendor id is the primary key) */
export const vendorService = {
  list: async (params) => {
    const response = await apiClient.get('/admin/vendors', { params })
    return response.data
  },
  create: async (data) => {
    const response = await apiClient.post('/admin/vendors', data)
    return response.data
  },
  update: async (vendorId, data) => {
    const response = await apiClient.put(
      `/admin/vendors/${encodeURIComponent(vendorId)}`,
      data,
    )
    return response.data
  },
  remove: async (vendorId) => {
    const response = await apiClient.delete(
      `/admin/vendors/${encodeURIComponent(vendorId)}`,
    )
    return response.data
  },
  importFile: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post('/admin/vendors/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
  downloadTemplate: async () => {
    const response = await apiClient.get('/admin/vendors/template', {
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const a = document.createElement('a')
    a.href = url
    a.download = 'vendor-template.xlsx'
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  },
}

/** Super-admin only: negative sales org policy */
export const salesPolicyService = {
  list: async (params) => {
    const response = await apiClient.get('/admin/sales-policy', { params })
    return response.data
  },
  get: async (orgId) => {
    const response = await apiClient.get(`/admin/sales-policy/${orgId}`)
    return response.data
  },
  update: async (orgId, allow_negative_sales) => {
    const response = await apiClient.put(`/admin/sales-policy/${orgId}`, {
      allow_negative_sales,
    })
    return response.data
  },
  bulk: async ({ mode, org_ids, allow_negative_sales }) => {
    const response = await apiClient.put('/admin/sales-policy/bulk', {
      mode,
      org_ids,
      allow_negative_sales,
    })
    return response.data
  },
  negativeSalesReport: async (params) => {
    const response = await apiClient.get('/admin/reports/negative-sales', {
      params,
    })
    return response.data
  },
  createNegativeSalesExport: async (payload) => {
    const response = await apiClient.post(
      '/admin/reports/negative-sales/export',
      payload,
    )
    return response.data
  },
  getNegativeSalesExportJobs: async (params = {}) => {
    const response = await apiClient.get(
      '/admin/reports/negative-sales/export-jobs',
      { params },
    )
    return response.data
  },
  getNegativeSalesExportJobUrl: async (id) => {
    const response = await apiClient.get(
      `/admin/reports/negative-sales/export-jobs/${id}/download`,
    )
    return response.data
  },
  decisionTimeline: async (decisionId) => {
    const response = await apiClient.get(
      `/sale-line-decisions/${decisionId}/timeline`,
    )
    return response.data
  },
}

export const criticalAuditService = {
  list: async (params = {}) => {
    const response = await apiClient.get('/admin/critical-audit', { params })
    return response.data
  },
  exportCsv: async (params = {}) => {
    const response = await apiClient.get('/admin/critical-audit/export', {
      params,
      responseType: 'blob',
    })
    return response
  },
}

export const adminBulkRbacService = {
  listAccessTokens: async () => {
    const response = await apiClient.get('/admin/bulk-rbac/access-tokens')
    return response.data
  },
  listRolesReference: async () => {
    const response = await apiClient.get('/admin/bulk-rbac/roles-reference')
    return response.data
  },
  importRoleAssignments: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post('/admin/bulk-rbac/assign/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
  importRoleCreations: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post('/admin/bulk-rbac/create-roles/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
}

export const adminAccessService = {
  getAssignableContext: async (googleUserId) => {
    const response = await apiClient.get('/admin/access/assignable-context', {
      params: googleUserId != null ? { googleUserId } : {},
    })
    return response.data
  },
  listGoogleUsers: async (params) => {
    const response = await apiClient.get('/admin/access/google-users', { params })
    return response.data
  },
  setUserRoles: async (googleUserId, roleCodes) => {
    const response = await apiClient.post(
      `/admin/access/users/${googleUserId}/roles`,
      { roleCodes }
    )
    return response.data
  },
  createRole: async (data) => {
    const response = await apiClient.post('/admin/roles', data)
    return response.data
  },
  listRoles: async () => {
    const response = await apiClient.get('/admin/roles')
    return response.data
  },
  updateRole: async (roleId, data) => {
    const response = await apiClient.patch(`/admin/roles/${roleId}`, data)
    return response.data
  },
  deleteRole: async (roleId) => {
    const response = await apiClient.delete(`/admin/roles/${roleId}`)
    return response.data
  },
}

// Product Services
export const productService = {
  importProducts: async (data) => {
    const formData = new FormData()
    formData.append('file', data.file)
    // Add batch size if provided
    if (data.batchSize) {
      formData.append('batchSize', data.batchSize)
    }
    const response = await apiClient.post('/admin/admin-products/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  forceDeleteProduct: async (id) => {
    const response = await apiClient.delete(`/admin/admin-products/${id}/force`)
    return response.data
  },

  getProducts: async (params) => {
    const response = await apiClient.get('/admin/admin-products', { params })
    return response.data
  },

  getAllProducts: async (params) => {
    const response = await apiClient.get('/admin/admin-products/all', { params })
    return response.data
  },

  getMyUploads: async (params) => {
    const response = await apiClient.get('/admin/admin-products/my-uploads', {
      params,
    })
    return response.data
  },

  createDownloadJob: async (payload) => {
    const response = await apiClient.post(
      '/admin/admin-products/download-jobs',
      payload,
    )
    return response.data
  },

  getDownloadJobs: async (params) => {
    const response = await apiClient.get('/admin/admin-products/download-jobs', {
      params,
    })
    return response.data
  },

  getDownloadJobUrl: async (id) => {
    const response = await apiClient.get(
      `/admin/admin-products/download-jobs/${id}/download`,
    )
    return response.data
  },

  getProductById: async (id) => {
    const response = await apiClient.get(`/admin/admin-products/${id}`)
    return response.data
  },

  getProductBarcodes: async (id) => {
    const response = await apiClient.get(`/admin/admin-products/${id}/barcodes`)
    return response.data
  },

  replaceProductBarcodes: async (id, barcodes) => {
    const response = await apiClient.put(`/admin/admin-products/${id}/barcodes`, {
      barcodes,
    })
    return response.data
  },

  downloadBulkBarcodeSample: async () => {
    const response = await apiClient.get('/admin/admin-products/barcodes/bulk/sample', {
      responseType: 'blob',
    })
    return response
  },

  uploadBulkBarcodes: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post(
      '/admin/admin-products/barcodes/bulk/import',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data
  },

  getBulkBarcodeUploadLogs: async (params) => {
    const uploadParams = { ...params, type: 'barcode' }
    const response = await apiClient.get('/admin/upload-logs', { params: uploadParams })
    return response.data
  },

  getBulkBarcodeUploadLogDetails: async (id) => {
    const response = await apiClient.get(`/admin/upload-logs/${id}`)
    return response.data
  },

  downloadBulkProductEditSample: async () => {
    const response = await apiClient.get('/admin/admin-products/bulk-edit/sample', {
      responseType: 'blob',
    })
    return response
  },

  importPurchasableStatus: async (data) => {
    const formData = new FormData()
    formData.append('file', data.file)
    const response = await apiClient.post(
      '/admin/admin-products/purchasable-status/import',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data
  },

  getPurchasableStatusUploadLogs: async (params) => {
    const uploadParams = { ...params, type: 'purchasable_status' }
    const response = await apiClient.get('/admin/upload-logs', { params: uploadParams })
    return response.data
  },

  uploadBulkProductEdit: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post(
      '/admin/admin-products/bulk-edit/import',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data
  },

  getBulkProductEditUploadLogs: async (params) => {
    const uploadParams = { ...params, type: 'product_update' }
    const response = await apiClient.get('/admin/upload-logs', { params: uploadParams })
    return response.data
  },

  getBulkProductEditUploadLogDetails: async (id) => {
    const response = await apiClient.get(`/admin/upload-logs/${id}`)
    return response.data
  },

  getProductDetails: async (id) => {
    const response = await apiClient.get(`/admin/admin-products/${id}/details`)
    return response.data
  },

  updateProduct: async (id, data) => {
    const response = await apiClient.put(`/admin/admin-products/${id}`, data)
    return response.data
  },

  deleteProduct: async (id, options = {}) => {
    const params = options.cascade ? { cascade: true } : {}
    const response = await apiClient.delete(`/admin/admin-products/${id}`, { params })
    return response.data
  },

  approveProduct: async (id) => {
    const response = await apiClient.post(`/admin/admin-products/${id}/approve`)
    return response.data
  },

  rejectProduct: async (id) => {
    const response = await apiClient.post(`/admin/admin-products/${id}/reject`)
    return response.data
  },

  // Upload logs methods
  getUploadLogs: async (params) => {
    const uploadParams = { ...params, type: 'product' }
    const response = await apiClient.get('/admin/upload-logs', { params: uploadParams })
    return response.data
  },

  getUploadLogById: async (id) => {
    const response = await apiClient.get(`/admin/upload-logs/${id}`)
    return response.data
  },

  getUploadLogDetails: async (id) => {
    const response = await apiClient.get(`/admin/upload-logs/${id}`)
    return response.data
  },

  getAllUploadLogs: async (params) => {
    const uploadParams = { ...params, type: 'product' }
    const response = await apiClient.get('/admin/all-upload-logs', { params: uploadParams })
    return response.data
  },
}

// Inventory Services
export const inventoryService = {
  importInventory: async (data) => {
    const formData = new FormData()
    formData.append('file', data.file)
    if (data.batchSize) {
      formData.append('batchSize', data.batchSize)
    }
    const response = await apiClient.post('/admin/inventory/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  getInventory: async (params) => {
    const response = await apiClient.get('/admin/inventory', { params })
    return response.data
  },

  getInventoryByOrganization: async (organizationId, params) => {
    const response = await apiClient.get(`/admin/inventory/${organizationId}`, {
      params,
    })
    return response.data
  },

  updateInventory: async (id, data) => {
    const response = await apiClient.put(`/inventory/${id}`, data)
    return response.data
  },

  // Admin inventory methods
  updateAdminInventory: async (sku, data) => {
    const response = await apiClient.put(`/admin/inventory/${sku}`, data)
    return response.data
  },

  updateAdminInventoryByLotId: async (lotId, data) => {
    const response = await apiClient.put(`/admin/inventory/lot/${lotId}`, data)
    return response.data
  },

  createOrUpdateInventory: async (sku, data) => {
    const response = await apiClient.put(`/admin/inventory/${sku}`, data)
    return response.data
  },

  getInventoryBySku: async (sku) => {
    const response = await apiClient.get(`/admin/inventory/${sku}`)
    return response.data
  },

  getInventoryPriceOptions: async (sku, params = {}) => {
    const response = await apiClient.get(
      `/admin/inventory/${encodeURIComponent(sku)}/price-options`,
      { params },
    )
    return response.data
  },

  getInventoryLotById: async (id) => {
    const response = await apiClient.get(`/admin/inventory/lot/${id}`)
    return response.data
  },

  getInventoryLotMovements: async (id, params) => {
    const response = await apiClient.get(`/admin/inventory/lot/${id}/movements`, { params })
    return response.data
  },

  getInventoryMovements: async (sku, params = {}) => {
    const response = await apiClient.get(`/admin/inventory/${sku}/movements`, { params })
    return response.data
  },

  getInventoryWithMovements: async (sku, params = {}) => {
    const response = await apiClient.get(`/admin/inventory/${sku}/with-movements`, { params })
    return response.data
  },

  deleteInventory: async (id) => {
    const response = await apiClient.delete(`/admin/admin-inventory/${id}`)
    return response.data
  },

  getUploadLogs: async (params) => {
    const uploadParams = { ...params, type: 'inventory' }
    const response = await apiClient.get('/admin/upload-logs', { params: uploadParams })
    return response.data
  },

  getUploadLogById: async (id) => {
    const response = await apiClient.get(`/admin/upload-logs/${id}`)
    return response.data
  },

  getUploadLogDetails: async (id) => {
    const response = await apiClient.get(`/admin/upload-logs/${id}`)
    return response.data
  },

  getAllUploadLogs: async (params) => {
    const uploadParams = { ...params, type: 'inventory' }
    const response = await apiClient.get('/admin/all-upload-logs', { params: uploadParams })
    return response.data
  },
}

// Reports Services
export const reportsService = {
  getSalesReport: async (params) => {
    const response = await apiClient.get('/reports/admin/sales', { params })
    return response.data
  },

  getInventoryReport: async (params) => {
    const response = await apiClient.get('/reports/admin/inventory', { params })
    return response.data
  },

  getCustomerReport: async (params) => {
    const response = await apiClient.get('/reports/admin/customers', { params })
    return response.data
  },

  getSupplierReport: async (params) => {
    const response = await apiClient.get('/reports/admin/suppliers', { params })
    return response.data
  },

  getOrganizationStats: async () => {
    const response = await apiClient.get('/admin/stats')
    return response.data
  },

  getExecutiveDashboard: async (params = {}) => {
    const { signal, ...query } = params
    const response = await apiClient.get('/admin/dashboard', { params: query, signal })
    return response.data
  },

  getUndeliveredLines: async (params = {}) => {
    const { signal, ...query } = params
    const response = await apiClient.get('/admin/dashboard/undelivered-lines', {
      params: query,
      signal,
    })
    return response.data
  },

  getAdminReports: async () => {
    const response = await apiClient.get('/reports/admin/overview')
    return response.data
  },

  getSalesInvoiceWiseReport: async (params) => {
    const response = await apiClient.get('/reports/admin/sales/invoices', { params })
    return response.data
  },

  getSalesProductWiseReport: async (params) => {
    const response = await apiClient.get('/reports/admin/sales/products', { params })
    return response.data
  },

  getInventoryStockReport: async (params) => {
    const response = await apiClient.get('/reports/admin/inventory/stock', { params })
    return response.data
  },

  getInventoryStockLineItems: async (inventoryId, params) => {
    const response = await apiClient.get(`/reports/admin/inventory/stock/${inventoryId}/line-items`, {
      params,
    })
    return response.data
  },

  getSalesInvoiceItems: async (orderId) => {
    const response = await apiClient.get(`/reports/admin/sales/invoices/${orderId}/items`)
    return response.data
  },

  getSalesInvoiceDetail: async (orderId) => {
    const response = await apiClient.get(`/reports/admin/sales/invoices/${orderId}/detail`)
    return response.data
  },

  exportSalesTable: async (params) => {
    const response = await apiClient.get('/reports/admin/sales/export/table', {
      params,
      responseType: 'blob',
    })
    return response
  },

  exportSalesCompiled: async (payload) => {
    const response = await apiClient.post(
      '/reports/admin/sales/export/compiled',
      payload,
    )
    return response.data
  },

  getSalesCompiledExportJobs: async ({ page, limit } = {}) => {
    const params = {}
    if (page !== undefined) params.page = page
    if (limit !== undefined) params.limit = limit
    const response = await apiClient.get('/reports/admin/sales/export-jobs', {
      params,
    })
    return response.data
  },

  getSalesCompiledExportJobUrl: async (id) => {
    const response = await apiClient.get(
      `/reports/admin/sales/export-jobs/${id}/download`,
    )
    return response.data
  },
}

export const modifyUserInventoryService = {
  getInventoryList: async (orgId, params) => {
    const response = await apiClient.get(`/admin/user-inventory/org/${orgId}`, { params })
    return response.data
  },

  updateInventoryPrices: async (orgId, inventoryId, data) => {
    const response = await apiClient.patch(
      `/admin/user-inventory/org/${orgId}/${inventoryId}/prices`,
      data,
    )
    return response.data
  },

  getInventoryPriceAuditLogs: async (orgId, inventoryId, params) => {
    const response = await apiClient.get(
      `/admin/user-inventory/org/${orgId}/${inventoryId}/price-audit-logs`,
      { params },
    )
    return response.data
  },

  getInventoryLineItems: async (orgId, inventoryId, params) => {
    const response = await apiClient.get(
      `/admin/user-inventory/org/${orgId}/${inventoryId}/line-items`,
      { params },
    )
    return response.data
  },

  updateInventoryLineItem: async (orgId, inventoryId, lineItemId, data) => {
    const response = await apiClient.patch(
      `/admin/user-inventory/org/${orgId}/${inventoryId}/line-items/${lineItemId}`,
      data,
    )
    return response.data
  },

  exportInventoryCsv: async (orgId, params) => {
    const response = await apiClient.get(`/admin/user-inventory/org/${orgId}/export`, {
      params,
      responseType: 'blob',
    })
    return response
  },

  importBulkPricesCsv: async (orgId, file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post(
      `/admin/user-inventory/org/${orgId}/bulk-prices/import`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
    return response.data
  },

  inlineBulkUpdatePrices: async (orgId, updates) => {
    const response = await apiClient.patch(
      `/admin/user-inventory/org/${orgId}/bulk-prices`,
      { updates },
    )
    return response.data
  },
}

/** Org-level MRP overrides for catalog-only negative sales (SCRUM-839). */
export const negativeSaleMrpService = {
  list: async (orgId, params) => {
    const response = await apiClient.get(`/admin/org-negative-sale-mrp/org/${orgId}`, {
      params,
    })
    return response.data
  },

  upsert: async (orgId, adminProductId, data) => {
    const response = await apiClient.put(
      `/admin/org-negative-sale-mrp/org/${orgId}/${adminProductId}`,
      data,
    )
    return response.data
  },

  remove: async (orgId, adminProductId) => {
    const response = await apiClient.delete(
      `/admin/org-negative-sale-mrp/org/${orgId}/${adminProductId}`,
    )
    return response.data
  },
}

// Organization Services
export const organizationService = {
  getOrganizations: async (params) => {
    const response = await apiClient.get('/admin/organizations', { params })
    return response.data
  },

  /** Paginated list with items + pagination (GET /admin/organizations). */
  getOrganizationsPage: async (params) => {
    const data = await organizationService.getOrganizations(params)
    if (Array.isArray(data)) {
      return {
        items: data,
        pagination: {
          page: 1,
          limit: data.length,
          total: data.length,
          hasNextPage: false,
          hasPrevPage: false,
        },
      }
    }
    const items =
      data?.items ||
      data?.organizations ||
      data?.data?.items ||
      (Array.isArray(data?.data) ? data.data : [])
    return {
      items: Array.isArray(items) ? items : [],
      pagination: data?.pagination || {
        page: 1,
        limit: items?.length || 0,
        total: items?.length || 0,
        hasNextPage: false,
        hasPrevPage: false,
      },
    }
  },

  getOrganizationById: async (id) => {
    const response = await apiClient.get(`/admin/organizations/${id}`)
    return response.data
  },

  getOrganizationUsers: async (organizationId, params) => {
    const response = await apiClient.get(
      `/admin/organizations/${organizationId}/users`,
      { params }
    )
    return response.data
  },
}

export const purchaseCatalogService = {
  getCategories: async () => {
    const response = await apiClient.get('/admin/purchase-catalog/categories')
    return response.data
  },

  getCatalog: async (orgId, { page = 1, limit = 20, category, q } = {}) => {
    const params = {
      orgId: String(orgId),
      page,
      limit,
    }
    if (category?.trim()) {
      params.category = category.trim()
    }
    if (q?.trim()) {
      params.q = q.trim()
    }
    const response = await apiClient.get('/admin/purchase-catalog', { params })
    return response.data
  },

  searchCatalog: async (orgId, { page = 1, limit = 20, q, category } = {}) => {
    const params = {
      orgId: String(orgId),
      page,
      limit,
    }
    if (q?.trim()) {
      params.q = q.trim()
    }
    if (category?.trim()) {
      params.category = category.trim()
    }
    try {
      const response = await apiClient.get('/admin/purchase-catalog/search', {
        params,
      })
      return response.data
    } catch (err) {
      if (q?.trim()) {
        return purchaseCatalogService.getCatalog(orgId, {
          page,
          limit,
          category,
          q,
        })
      }
      throw err
    }
  },

  previewFulfillment: async (orgId, items) => {
    const response = await apiClient.post(
      '/admin/purchase-catalog/fulfillment-preview',
      { items },
      { params: { orgId: String(orgId) } },
    )
    return response.data
  },

  /**
   * Resolve SKU for admin purchase order (mapped lots + outside-mapped fallback).
   * @param {string|number} orgId
   * @param {string} sku
   * @param {number} [quantity=1]
   */
  resolveSku: async (orgId, sku, quantity = 1) => {
    const response = await apiClient.get('/admin/purchase-catalog/resolve', {
      params: {
        orgId: String(orgId),
        sku: String(sku).trim(),
        quantity,
      },
    })
    return response.data
  },
}

// Admin Order Services
export const adminOrderService = {
  getOrders: async ({ page, limit, status }) => {
    const params = {}
    if (page !== undefined) params.page = page
    if (limit !== undefined) params.limit = limit
    if (status !== undefined) params.status = status
    const response = await apiClient.get('/admin/orders', { params })
    return response.data
  },

  getOrderById: async (orderId) => {
    const response = await apiClient.get(`/admin/orders/${orderId}`)
    return response.data
  },

  getOrderPayment: async (orderId) => {
    const response = await apiClient.get(`/admin/orders/${orderId}/payment`)
    return response.data
  },

  getOrderPricingTimeline: async (orderId) => {
    const response = await apiClient.get(`/admin/orders/${orderId}/pricing-timeline`)
    return response.data
  },

  getOrderPricingAudit: async (orderId) => {
    const response = await apiClient.get(`/admin/orders/${orderId}/pricing-audit`)
    return response.data
  },

  getOrderModificationHistory: async (orderId) => {
    const response = await apiClient.get(
      `/admin/orders/${orderId}/modification-history`,
    )
    return response.data
  },

  getOrderTimeline: async (orderId) => {
    const response = await apiClient.get(`/admin/orders/${orderId}/timeline`)
    return response.data
  },

  exportPurchaseOrder: async (orderId, format = 'xlsx') => {
    // Async job — returns { jobId, status, transport }, not a file blob.
    const response = await apiClient.get(`/admin/orders/${orderId}/export`, {
      params: { format },
    })
    return response.data
  },

  exportPurchaseOrders: async (payload, format = 'xlsx') => {
    const body = Array.isArray(payload)
      ? { mode: 'ids', order_ids: payload, format }
      : payload
    const response = await apiClient.post('/admin/orders/export', body)
    return response.data
  },

  getPurchaseOrderExportJobs: async ({ page, limit } = {}) => {
    const params = {}
    if (page !== undefined) params.page = page
    if (limit !== undefined) params.limit = limit
    const response = await apiClient.get('/admin/orders/export-jobs', { params })
    return response.data
  },

  getPurchaseOrderExportJobUrl: async (id) => {
    const response = await apiClient.get(`/admin/orders/export-jobs/${id}/download`)
    return response.data
  },

  createPurchaseOrder: async ({ org_id, items, allow_insufficient_stock = true }) => {
    const response = await apiClient.post('/admin/orders/create-purchase-order', {
      org_id,
      items,
      allow_insufficient_stock,
    })
    return response.data
  },

  getPurchaseCreditSettings: async () => {
    const response = await apiClient.get('/admin/settings/purchase-credit')
    return response.data
  },

  updatePurchaseCreditSettings: async (purchaseCreditApplyPercent) => {
    const response = await apiClient.put('/admin/settings/purchase-credit', {
      purchase_credit_apply_percent: purchaseCreditApplyPercent,
    })
    return response.data
  },

  getInventoryGlobalSettings: async () => {
    const response = await apiClient.get('/admin/settings/inventory')
    return response.data
  },

  updateInventoryGlobalSettings: async (lowStockThreshold) => {
    const response = await apiClient.put('/admin/settings/inventory', {
      low_stock_threshold: lowStockThreshold,
    })
    return response.data
  },

  previewCredit: async (orderId) => {
    const response = await apiClient.get(`/admin/orders/${orderId}/credit-preview`)
    return response.data
  },

  issueCredit: async (orderId, { amount_override, expires_at, reason } = {}) => {
    const response = await apiClient.post(`/admin/orders/${orderId}/issue-credit`, {
      amount_override,
      expires_at,
      reason,
    })
    return response.data
  },

  updateCreditNoteExpiry: async (noteId, expiresAt) => {
    const response = await apiClient.put(`/admin/purchase-credit-notes/${noteId}/expiry`, {
      expires_at: expiresAt,
    })
    return response.data
  },

  acceptOrder: async (orderId) => {
    const response = await apiClient.post(`/admin/orders/${orderId}/accept`, {
      itemAdjustments: [],
    })
    return response.data
  },

  modifyAcceptOrder: async (orderId, itemAdjustments = [], newLineItems = []) => {
    const response = await apiClient.post(`/admin/orders/${orderId}/modify-accept`, {
      itemAdjustments,
      newLineItems,
    })
    return response.data
  },

  updateLineQuantities: async (orderId, adjustments = []) => {
    const response = await apiClient.patch(
      `/admin/orders/${orderId}/line-quantities`,
      { adjustments },
    )
    return response.data
  },

  rejectOrder: async (orderId, reason) => {
    const body = reason ? { reason } : {}
    const response = await apiClient.post(`/admin/orders/${orderId}/reject`, body)
    return response.data
  },

  startProcurement: async (orderId) => {
    const response = await apiClient.post(`/admin/orders/${orderId}/start-procurement`, {})
    return response.data
  },

  markReadyForDispatch: async (orderId) => {
    const response = await apiClient.post(`/admin/orders/${orderId}/ready-for-dispatch`, {})
    return response.data
  },

  dispatchOrder: async (
    orderId,
    items = null,
    referenceFields = null,
    locationId = null,
    options = null,
  ) => {
    const body = {}
    if (items?.length) body.items = items
    if (locationId != null) body.location_id = Number(locationId)
    if (referenceFields) {
      body.dn_reference = referenceFields.dn_reference
      body.tpn_number = referenceFields.tpn_number
      body.invoice_number = referenceFields.invoice_number
    }
    if (options?.acknowledgeAutoProvision) {
      body.acknowledge_auto_provision = true
    }
    if (options?.soNumber) {
      body.so_number = options.soNumber
      body.reason = options.reason
    }
    if (options?.vendorId) {
      body.vendor_id = options.vendorId
    }
    if (options?.dsdShipment) {
      body.dsd_shipment = true
    }
    const response = await apiClient.post(
      `/admin/orders/${orderId}/dispatch`,
      body,
    )
    return response.data
  },

  createShipment: async (
    orderId,
    items,
    referenceFields = null,
    locationId = null,
    options = null,
  ) => {
    const body = { items }
    if (locationId != null) body.location_id = Number(locationId)
    if (referenceFields) {
      body.dn_reference = referenceFields.dn_reference
      body.tpn_number = referenceFields.tpn_number
      body.invoice_number = referenceFields.invoice_number
    }
    if (options?.acknowledgeAutoProvision) {
      body.acknowledge_auto_provision = true
    }
    if (options?.soNumber) {
      body.so_number = options.soNumber
      body.reason = options.reason
    }
    if (options?.vendorId) {
      body.vendor_id = options.vendorId
    }
    if (options?.dsdShipment) {
      body.dsd_shipment = true
    }
    const response = await apiClient.post(
      `/admin/orders/${orderId}/shipments`,
      body,
    )
    return response.data
  },

  /** SCRUM-760: Complete and lock order (requires confirmed shipment + reason). */
  completeOrder: async (orderId, { reason } = {}) => {
    const response = await apiClient.post(`/admin/orders/${orderId}/complete`, {
      reason,
    })
    return response.data
  },

  getShipmentAvailability: async (orderId, locationId, productIds = []) => {
    const response = await apiClient.get(
      `/admin/orders/${orderId}/shipment-availability`,
      {
        params: {
          location_id: locationId,
          ...(productIds?.length
            ? { product_ids: productIds.join(',') }
            : {}),
        },
      },
    )
    return response.data
  },

  listShipments: async (orderId) => {
    const response = await apiClient.get(`/admin/orders/${orderId}/shipments`)
    return response.data
  },

  retrySalesOrderRecording: async (orderId, shipmentId) => {
    const response = await apiClient.post(
      `/admin/orders/${orderId}/shipments/${shipmentId}/sales-order-recording`,
    )
    return response.data
  },

  getSalesOrderRecording: async (orderId, shipmentId) => {
    const response = await apiClient.get(
      `/admin/orders/${orderId}/shipments/${shipmentId}/sales-order-recording`,
    )
    return response.data
  },

  getSapSalesOrderPayload: async (orderId, shipmentId) => {
    const response = await apiClient.get(
      `/admin/orders/${orderId}/shipments/${shipmentId}/sales-order-payload`,
    )
    return response.data
  },

  /** Outbound create payload stored on a SHIPMENT_CREATE_FAILED timeline row. */
  getShipmentCreateFailureRequest: async (orderId, transitionId) => {
    const response = await apiClient.get(
      `/admin/orders/${orderId}/timeline/${transitionId}/shipment-create-failure/request`,
    )
    return response.data
  },

  /** Whole api-manager / SAP create response for a SHIPMENT_CREATE_FAILED timeline row. */
  getShipmentCreateFailureResponse: async (orderId, transitionId) => {
    const response = await apiClient.get(
      `/admin/orders/${orderId}/timeline/${transitionId}/shipment-create-failure/response`,
    )
    return response.data
  },

  getPurchaseOrderUpdate: async (orderId, shipmentId) => {
    const response = await apiClient.get(
      `/admin/orders/${orderId}/shipments/${shipmentId}/purchase-order-update`,
    )
    return response.data
  },

  getAspenReceiveItems: async (orderId, shipmentId) => {
    const response = await apiClient.get(
      `/admin/orders/${orderId}/shipments/${shipmentId}/receive-items`,
    )
    return response.data
  },

  retryAspenReceiveItems: async (orderId, shipmentId) => {
    const response = await apiClient.post(
      `/admin/orders/${orderId}/shipments/${shipmentId}/receive-items`,
    )
    return response.data
  },

  updateShipmentReferenceFields: async (orderId, shipmentId, fields) => {
    const response = await apiClient.put(
      `/admin/orders/${orderId}/shipments/${shipmentId}/reference-fields`,
      {
        dn_reference: fields.dn_reference,
        tpn_number: fields.tpn_number,
        invoice_number: fields.invoice_number,
      },
    )
    return response.data
  },

  uploadShipmentProofOfDelivery: async (orderId, shipmentId, files = []) => {
    const formData = new FormData()
    ;(files || []).forEach((file) => formData.append('files', file))
    const response = await apiClient.post(
      `/admin/orders/${orderId}/shipments/${shipmentId}/proof-of-delivery`,
      formData,
      {
        transformRequest: [
          (data, headers) => {
            if (data instanceof FormData) {
              delete headers['Content-Type']
            }
            return data
          },
        ],
      },
    )
    return response.data
  },

  deleteShipmentProofOfDelivery: async (orderId, shipmentId, fileId) => {
    const response = await apiClient.delete(
      `/admin/orders/${orderId}/shipments/${shipmentId}/proof-of-delivery/${fileId}`,
    )
    return response.data
  },

  markReturnShipmentReceived: async (orderId) => {
    const response = await apiClient.post(`/admin/orders/${orderId}/return-shipment-received`, {})
    return response.data
  },

  confirmOrderReceived: async (orderId, { items, shipment_id, notes }) => {
    const response = await apiClient.post(`/admin/orders/${orderId}/confirm-received`, {
      items,
      ...(shipment_id != null ? { shipment_id } : {}),
      ...(notes ? { notes } : {}),
    })
    return response.data
  },

  submitManualWebhook: async (orderId, shipmentId, body) => {
    const response = await apiClient.post(
      `/admin/orders/${orderId}/shipments/${shipmentId}/manual-webhook`,
      body,
    )
    return response.data
  },

  reconcileMissingItems: async (orderId, reconcileItems) => {
    const response = await apiClient.post(`/admin/orders/${orderId}/reconcile-missing`, {
      reconcile_items: reconcileItems,
    })
    return response.data
  },

  reconcileDamagedItems: async (orderId, reconcileItems) => {
    const response = await apiClient.post(`/admin/orders/${orderId}/reconcile-damaged`, {
      reconcile_items: reconcileItems,
    })
    return response.data
  },

  /**
   * Admin upload of payment proof (same meta.payment_upload as mobile). Max 5 URLs total.
   * @param {number|string} orderId
   * @param {File[]} files - new files (can be empty if only updating kept URLs)
   * @param {string[]} existingUrls - URLs to retain (subset of current proofs when removing)
   * @param {string} paymentProofDateTime - ISO-8601 uploader-selected payment datetime
   */
  getPaymentProofReceivingAccounts: async () => {
    const response = await apiClient.get('/admin/payment-proof/receiving-accounts')
    return response.data
  },

  getPaymentProofBanks: async () => {
    const response = await apiClient.get('/admin/payment-proof/banks')
    return response.data
  },

  getOrderSavedPaymentAccounts: async (orderId) => {
    const response = await apiClient.get(
      `/admin/orders/${orderId}/payment-proof/saved-accounts`,
    )
    return response.data
  },

  /**
   * Branches for a bank. Pass { q, limit, offset } to search (server
   * returns { data, total, limit, offset }); without params returns every branch.
   */
  getPaymentProofBranches: async (bankId, { q, limit, offset } = {}) => {
    const params = {}
    if (q) params.q = q
    if (limit != null) params.limit = limit
    if (offset != null) params.offset = offset
    const response = await apiClient.get(
      `/admin/payment-proof/banks/${bankId}/branches`,
      Object.keys(params).length ? { params } : undefined,
    )
    return response.data
  },

  getPaymentProofBranchByRouting: async (routing) => {
    const response = await apiClient.get(
      `/admin/payment-proof/branches/routing/${encodeURIComponent(routing)}`,
    )
    return response.data
  },

  applyExistingBalance: async (orderId) => {
    const response = await apiClient.post(
      `/admin/orders/${orderId}/apply-existing-balance`,
      {},
    )
    return response.data
  },

  uploadPaymentProofStructured: async (orderId, files = [], proofs = []) => {
    const formData = new FormData()
    ;(files || []).forEach((f) => formData.append('files', f))
    if (proofs?.length) {
      formData.append('proofs', JSON.stringify(proofs))
    }
    const response = await apiClient.post(
      `/admin/orders/${orderId}/upload-payment-proof`,
      formData,
      {
        transformRequest: [
          (data, headers) => {
            if (data instanceof FormData) {
              delete headers['Content-Type']
            }
            return data
          },
        ],
      },
    )
    return response.data
  },

  uploadPaymentProof: async (
    orderId,
    files = [],
    existingUrls = [],
    paymentProofDateTime,
  ) => {
    const formData = new FormData()
    ;(files || []).forEach((f) => formData.append('files', f))
    if (existingUrls?.length) {
      formData.append('existing_urls', JSON.stringify(existingUrls))
    }
    if (paymentProofDateTime) {
      formData.append('payment_proof_datetime', paymentProofDateTime)
    }
    const response = await apiClient.post(
      `/admin/orders/${orderId}/upload-payment-proof`,
      formData,
      {
        transformRequest: [
          (data, headers) => {
            if (data instanceof FormData) {
              delete headers['Content-Type']
            }
            return data
          },
        ],
      },
    )
    return response.data
  },

  verifyPaymentProofRequest: async (orderId, requestId, payload) => {
    const response = await apiClient.post(
      `/admin/orders/${orderId}/payment-proof-requests/${requestId}/verify`,
      payload,
    )
    return response.data
  },

  verifyPaymentProofRequestById: async (requestId, payload) => {
    const response = await apiClient.post(
      `/admin/payment-proof-requests/${requestId}/verify`,
      payload,
    )
    return response.data
  },

  rejectPaymentProofRequestById: async (requestId, reason) => {
    const response = await apiClient.post(
      `/admin/payment-proof-requests/${requestId}/reject`,
      { reason },
    )
    return response.data
  },

  verifyPaymentProofById: async (requestId, proofId, payload) => {
    const response = await apiClient.post(
      `/admin/payment-proof-requests/${requestId}/proofs/${proofId}/verify`,
      payload,
    )
    return response.data
  },

  rejectPaymentProofById: async (requestId, proofId, reason) => {
    const response = await apiClient.post(
      `/admin/payment-proof-requests/${requestId}/proofs/${proofId}/reject`,
      { reason },
    )
    return response.data
  },

  listRejectedPaymentProofRequests: async (params = {}) => {
    const response = await apiClient.get('/admin/payment-proof-requests/rejected', {
      params,
    })
    return response.data
  },

  editRejectedPaymentProofRequest: async (orderId, requestId, proofs) => {
    const response = await apiClient.post(
      `/admin/orders/${orderId}/payment-proof-requests/${requestId}/edit`,
      { proofs },
    )
    return response.data
  },

  rejectPaymentProofRequest: async (orderId, requestId, reason) => {
    const response = await apiClient.post(
      `/admin/orders/${orderId}/payment-proof-requests/${requestId}/reject`,
      { reason },
    )
    return response.data
  },

  /** Superuser only: move a verified/rejected request back to pending. */
  reversePaymentProofRequest: async (orderId, requestId, reason) => {
    const response = await apiClient.post(
      `/admin/orders/${orderId}/payment-proof-requests/${requestId}/reverse`,
      { reason },
    )
    return response.data
  },

  listAdminDeposits: async (params) => {
    const response = await apiClient.get('/admin/deposits', { params })
    return response.data
  },

  exportAdminDepositsExcel: async (params) => {
    const response = await apiClient.get('/admin/deposits/export', {
      params,
      responseType: 'blob',
    })
    return response
  },

  getAdminDepositDetail: async (requestId) => {
    const response = await apiClient.get(`/admin/deposits/${requestId}`)
    return response.data?.deposit ?? response.data
  },

  listAdminDepositTaggableOrders: async (organizationId, { search } = {}) => {
    const response = await apiClient.get('/admin/deposits/taggable-orders', {
      params: {
        organization_id: organizationId,
        ...(search ? { search } : {}),
      },
    })
    return response.data
  },

  tagAdminShopDeposit: async (requestId, orderId) => {
    const response = await apiClient.patch(
      `/admin/deposits/${requestId}/tag`,
      { order_id: orderId },
    )
    return response.data
  },

  getShopSavedPaymentAccounts: async (organizationId) => {
    const response = await apiClient.get('/admin/deposits/saved-accounts', {
      params: { organization_id: organizationId },
    })
    return response.data
  },

  submitAdminShopDeposit: async (
    organizationId,
    files = [],
    { proofs = [], orderId } = {},
  ) => {
    const formData = new FormData()
    ;(files || []).forEach((f) => formData.append('files', f))
    formData.append('organization_id', String(organizationId))
    if (proofs?.length) {
      formData.append('proofs', JSON.stringify(proofs))
    }
    if (orderId != null && orderId !== '') {
      formData.append('order_id', String(orderId))
    }
    const response = await apiClient.post('/admin/deposits', formData, {
      transformRequest: [
        (data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type']
          }
          return data
        },
      ],
    })
    return response.data
  },

  editRejectedPaymentProofRequestById: async (requestId, proofs) => {
    const response = await apiClient.post(
      `/admin/payment-proof-requests/${requestId}/edit`,
      { proofs },
    )
    return response.data
  },

  reversePaymentProofRequestById: async (requestId, reason) => {
    const response = await apiClient.post(
      `/admin/payment-proof-requests/${requestId}/reverse`,
      { reason },
    )
    return response.data
  },
}

export const purchaseCreditNoteService = {
  list: async (params = {}) => {
    const response = await apiClient.get('/admin/purchase-credit-notes', { params })
    return response.data
  },

  createManual: async ({ organization_id, amount, expires_at, reason }) => {
    const response = await apiClient.post('/admin/purchase-credit-notes', {
      organization_id,
      amount,
      expires_at,
      reason,
    })
    return response.data
  },

  update: async (noteId, data) => {
    const response = await apiClient.patch(`/admin/purchase-credit-notes/${noteId}`, data)
    return response.data
  },

  delete: async (noteId, { delete_reason }) => {
    const response = await apiClient.delete(`/admin/purchase-credit-notes/${noteId}`, {
      data: { delete_reason },
    })
    return response.data
  },
}

export const priceConcernAdminService = {
  list: async (params = {}) => {
    const response = await apiClient.get('/admin/purchase-price-concerns', { params })
    return response.data
  },

  summary: async (params = {}) => {
    const response = await apiClient.get('/admin/purchase-price-concerns/summary', {
      params,
    })
    return response.data
  },

  updateStatus: async (id, status) => {
    const response = await apiClient.patch(
      `/admin/purchase-price-concerns/${id}/status`,
      { status },
    )
    return response.data
  },

  getStatusLogs: async (id) => {
    const response = await apiClient.get(
      `/admin/purchase-price-concerns/${id}/status-logs`,
    )
    return response.data
  },

  exportExcel: async (params = {}) => {
    const response = await apiClient.get('/admin/purchase-price-concerns/export', {
      params,
      responseType: 'blob',
    })
    return response
  },
  
}

// Order Services (legacy)
export const orderService = {
  getPaymentProcessingList: async (params) => {
    const response = await apiClient.get('/admin/payment-processing', { params })
    return response.data
  },

  listAdminDeposits: async (params) => {
    const response = await apiClient.get('/admin/deposits', { params })
    return response.data
  },

  getAdminDepositDetail: async (requestId) => {
    const response = await apiClient.get(`/admin/deposits/${requestId}`)
    return response.data?.deposit ?? response.data
  },

  editRejectedPaymentProofRequestById: async (requestId, proofs) => {
    const response = await apiClient.post(
      `/admin/payment-proof-requests/${requestId}/edit`,
      { proofs },
    )
    return response.data
  },

  reversePaymentProofRequestById: async (requestId, reason) => {
    const response = await apiClient.post(
      `/admin/payment-proof-requests/${requestId}/reverse`,
      { reason },
    )
    return response.data
  },

  exportPaymentProcessingExcel: async (params) => {
    const response = await apiClient.get('/admin/payment-processing/export', {
      params,
      responseType: 'blob',
    })
    return response
  },

  getAllOrders: async (params, { signal } = {}) => {
    const response = await apiClient.get('/admin/orders', { params, signal })
    return response.data
  },

  getUnreceivedDispatchAlerts: async (params) => {
    const response = await apiClient.get('/admin/orders/unreceived-dispatch-alerts', {
      params,
    })
    return response.data
  },

  getOrderById: async (id) => {
    const response = await apiClient.get(`/admin/orders/${id}`)
    return response.data
  },

  getOrderPaymentProof: async (id) => {
    const response = await apiClient.get(`/admin/orders/${id}/payment-proof`)
    return response.data
  },

  getOrderPayment: async (id) => {
    const response = await apiClient.get(`/admin/orders/${id}/payment`)
    return response.data
  },

  getOrderPricingTimeline: async (id) => {
    const response = await apiClient.get(`/admin/orders/${id}/pricing-timeline`)
    return response.data
  },

  getOrderPricingAudit: async (id) => {
    const response = await apiClient.get(`/admin/orders/${id}/pricing-audit`)
    return response.data
  },

  getOrderModificationHistory: async (id) => {
    const response = await apiClient.get(`/admin/orders/${id}/modification-history`)
    return response.data
  },

  getOrderTimeline: async (id) => {
    const response = await apiClient.get(`/admin/orders/${id}/timeline`)
    return response.data
  },

  acceptOrder: async (id, itemAdjustments = []) => {
    const response = await apiClient.post(`/admin/orders/${id}/accept`, {
      itemAdjustments,
    })
    return response.data
  },

  modifyAcceptOrder: async (id, itemAdjustments = [], newLineItems = []) => {
    const response = await apiClient.post(`/admin/orders/${id}/modify-accept`, {
      itemAdjustments,
      newLineItems,
    })
    return response.data
  },

  updateLineQuantities: async (id, adjustments = []) => {
    const response = await apiClient.patch(`/admin/orders/${id}/line-quantities`, {
      adjustments,
    })
    return response.data
  },

  rejectOrder: async (id, reason) => {
    const response = await apiClient.post(`/admin/orders/${id}/reject`, {
      reason: reason || 'Order rejected by admin',
    })
    return response.data
  },

  startProcurement: async (id) => {
    const response = await apiClient.post(`/admin/orders/${id}/start-procurement`, {})
    return response.data
  },

  readyForDispatch: async (id) => {
    const response = await apiClient.post(`/admin/orders/${id}/ready-for-dispatch`, {})
    return response.data
  },

  dispatchOrder: async (id, items = null, locationId = null) => {
    const body = items?.length ? { items } : {}
    if (locationId != null) body.location_id = Number(locationId)
    const response = await apiClient.post(`/admin/orders/${id}/dispatch`, body)
    return response.data
  },

  createShipment: async (id, items, locationId = null) => {
    const body = { items }
    if (locationId != null) body.location_id = Number(locationId)
    const response = await apiClient.post(
      `/admin/orders/${id}/shipments`,
      body,
    )
    return response.data
  },

  /** SCRUM-760: Complete and lock order (remaining_to_ship must be 0). */
  completeOrder: async (id, { reason } = {}) => {
    const response = await apiClient.post(`/admin/orders/${id}/complete`, {
      reason,
    })
    return response.data
  },

  rejectReturn: async (id, reason) => {
    const body = reason ? { reason } : {}
    const response = await apiClient.post(`/admin/orders/${id}/reject-return`, body)
    return response.data
  },

  acceptReturn: async (id) => {
    const response = await apiClient.post(`/admin/orders/${id}/accept-return`, {})
    return response.data
  },

  reconcileReturn: async (id, reconcileItems) => {
    const response = await apiClient.post(`/admin/orders/${id}/reconcile-return`, {
      reconcile_items: reconcileItems,
    })
    return response.data
  },
}

// Payment Settings Service
export const paymentSettingsService = {
  getAll: async (type) => {
    const params = type ? { type } : {}
    const response = await apiClient.get('/admin/payment-settings', { params })
    return response.data
  },

  getTypes: async () => {
    const response = await apiClient.get('/admin/payment-settings/types')
    return response.data
  },

  getById: async (id) => {
    const response = await apiClient.get(`/admin/payment-settings/${id}`)
    return response.data
  },

  create: async (data) => {
    const response = await apiClient.post('/admin/payment-settings', data)
    return response.data
  },

  update: async (id, data) => {
    const response = await apiClient.put(`/admin/payment-settings/${id}`, data)
    return response.data
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/admin/payment-settings/${id}`)
    return response.data
  },

  getAuditLogs: async (id) => {
    const response = await apiClient.get(`/admin/payment-settings/${id}/audit-logs`)
    return response.data
  },
}

// POS Store Promotion Services
export const posStorePromotionService = {
  list: async (params) => {
    const response = await apiClient.get('/admin/pos-promotions', { params })
    return response.data
  },
  lookupPromotion: async ({ promotionId, sku }) => {
    const response = await apiClient.get('/admin/pos-promotions/lookup', {
      params: { promotionId, sku },
    })
    return response.data
  },
  downloadSampleCsv: async () => {
    const response = await apiClient.get('/admin/upcoming-promotions/sample', {
      responseType: 'blob',
    })
    return response.data
  },
  importPromotions: async (data) => {
    const formData = new FormData()
    formData.append('file', data.file)
    if (data.batchSize) {
      formData.append('batchSize', data.batchSize)
    }
    const response = await apiClient.post('/admin/upcoming-promotions/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: data.batchSize ? { batchSize: data.batchSize } : undefined,
    })
    return response.data
  },
  getUploadLogs: async (params) => {
    const uploadParams = { ...params, type: 'upcoming_promotion' }
    const response = await apiClient.get('/admin/upload-logs', { params: uploadParams })
    return response.data
  },
  getUploadLogDetails: async (id) => {
    const response = await apiClient.get(`/admin/upload-logs/${id}`)
    return response.data
  },
  downloadUploadFile: async (id) => {
    const response = await apiClient.get(`/admin/upload-logs/${id}/file`, {
      responseType: 'blob',
    })
    return response
  },
  getReportSummary: async (params) => {
    const response = await apiClient.get('/reports/admin/pos-promotions/summary', {
      params,
    })
    return response.data
  },
  getSalesReport: async (params) => {
    const response = await apiClient.get('/reports/admin/pos-promotions/sales', {
      params,
    })
    return response.data
  },
  downloadReimbursementReport: async (params) => {
    const response = await apiClient.get(
      '/reports/admin/pos-promotions/reimbursement/export',
      {
        params,
        responseType: 'blob',
      },
    )
    return response
  },
  getPromoOrders: async (params) => {
    const response = await apiClient.get('/reports/admin/pos-promotions/orders', {
      params,
    })
    return response.data
  },
}

// Upcoming Promotion Services (advertising entity; not POS pricing)
export const upcomingPromotionService = {
  list: async (params) => {
    const response = await apiClient.get('/admin/upcoming-promotions', { params })
    return response.data
  },
  update: async (id, data) => {
    const response = await apiClient.put(`/admin/upcoming-promotions/${id}`, data)
    return response.data
  },
  listEditLogs: async (params) => {
    const response = await apiClient.get('/admin/upcoming-promotions/edit-logs', {
      params,
    })
    return response.data
  },
  export: async ({ format = 'xlsx', search } = {}) => {
    const body = { format }
    const trimmed = String(search || '').trim()
    if (trimmed) body.search = trimmed
    const response = await apiClient.post(
      '/admin/upcoming-promotions/export',
      body,
    )
    return response.data
  },
  getExportJobs: async ({ page, limit } = {}) => {
    const params = {}
    if (page !== undefined) params.page = page
    if (limit !== undefined) params.limit = limit
    const response = await apiClient.get(
      '/admin/upcoming-promotions/export-jobs',
      { params },
    )
    return response.data
  },
  getExportJobUrl: async (id) => {
    const response = await apiClient.get(
      `/admin/upcoming-promotions/export-jobs/${id}/download`,
    )
    return response.data
  },
  getReimbursementSettings: async () => {
    const response = await apiClient.get('/admin/promo-reimbursement-settings')
    return response.data
  },
  updateReimbursementSettings: async (showZeroLinesToGrocers) => {
    const response = await apiClient.put('/admin/promo-reimbursement-settings', {
      show_zero_lines_to_grocers: showZeroLinesToGrocers,
    })
    return response.data
  },
}

// Promotion Services
export const promotionService = {
  importPromotions: async (data) => {
    const formData = new FormData()
    formData.append('file', data.file)
    if (data.batchSize) {
      formData.append('batchSize', data.batchSize)
    }
    const response = await apiClient.post('/admin/promotions/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  getPromotions: async (params) => {
    const response = await apiClient.get('/admin/promotions', { params })
    return response.data
  },

  getPromotionById: async (id) => {
    const response = await apiClient.get(`/admin/promotions/${id}`)
    return response.data
  },

  createPromotion: async (data) => {
    const response = await apiClient.post('/admin/promotions', data)
    return response.data
  },

  updatePromotion: async (id, data) => {
    const response = await apiClient.put(`/admin/promotions/${id}`, data)
    return response.data
  },

  deletePromotion: async (id) => {
    const response = await apiClient.delete(`/admin/promotions/${id}`)
    return response.data
  },

  getUploadLogs: async (params) => {
    const uploadParams = { ...params, type: 'promotion' }
    const response = await apiClient.get('/admin/upload-logs', { params: uploadParams })
    return response.data
  },

  getUploadLogDetails: async (id) => {
    const response = await apiClient.get(`/admin/upload-logs/${id}`)
    return response.data
  },
}

// Location Services
export const locationService = {
  importLocations: async (data) => {
    const formData = new FormData()
    formData.append('file', data.file)
    if (data.batchSize) {
      formData.append('batchSize', data.batchSize)
    }
    const response = await apiClient.post('/location/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  getLocations: async (params) => {
    const response = await apiClient.get('/location', { params })
    return response.data
  },

  updateLocation: async (id, data) => {
    const response = await apiClient.put(`/location/${id}`, data)
    return response.data
  },

  deleteLocation: async (id, confirm = false) => {
    const params = confirm ? { confirm: 'true' } : {}
    const response = await apiClient.delete(`/location/${id}`, { params })
    return response.data
  },

  getUploadLogs: async (params) => {
    const uploadParams = { ...params, type: 'location' }
    const response = await apiClient.get('/admin/upload-logs', { params: uploadParams })
    return response.data
  },

  getUploadLogDetails: async (id) => {
    const response = await apiClient.get(`/admin/upload-logs/${id}`)
    return response.data
  },
}

// Store location mapping: flat org + location links
export const storeLocationMappingService = {
  list: async (params) => {
    const response = await apiClient.get('/store-location-mapping', { params })
    return response.data
  },
  listOrganizations: async (params) => {
    const response = await apiClient.get('/store-location-mapping/organizations', { params })
    return response.data
  },
  getById: async (id) => {
    const response = await apiClient.get(`/store-location-mapping/${id}`)
    return response.data
  },
  /** Paginated link rows with locations for one org */
  getOrgLocationLinks: async (orgId, params) => {
    const response = await apiClient.get(`/store-location-mapping/by-org/${orgId}/locations`, {
      params,
    })
    return response.data
  },
  /** All link rows for one org (edit dialog seed) */
  getOrgLocationLinksAll: async (orgId) => {
    const response = await apiClient.get(`/store-location-mapping/by-org/${orgId}/location-links`)
    return response.data
  },
  create: async (data) => {
    const response = await apiClient.post('/store-location-mapping', data)
    return response.data
  },
  replaceOrgLocations: async (orgId, data) => {
    const response = await apiClient.put(`/store-location-mapping/by-org/${orgId}/locations`, data)
    return response.data
  },
  removeAllByOrg: async (orgId) => {
    const response = await apiClient.delete(`/store-location-mapping/by-org/${orgId}`)
    return response.data
  },
  remove: async (id) => {
    const response = await apiClient.delete(`/store-location-mapping/${id}`)
    return response.data
  },
  getDefaultOrgLocationSettings: async () => {
    const response = await apiClient.get('/store-location-mapping/settings/default-location')
    return response.data
  },
  updateDefaultOrgLocationSettings: async (defaultLocationCode) => {
    const response = await apiClient.put('/store-location-mapping/settings/default-location', {
      default_location_code: defaultLocationCode,
    })
    return response.data
  },
}

// Category Services
export const categoryService = {
  importCategories: async (data) => {
    const formData = new FormData()
    formData.append('file', data.file)
    if (data.batchSize) {
      formData.append('batchSize', data.batchSize)
    }
    const response = await apiClient.post('/category/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  getCategories: async (params) => {
    const response = await apiClient.get('/category', { params })
    return response.data
  },

  updateCategory: async (id, data) => {
    const response = await apiClient.put(`/category/${id}`, data)
    return response.data
  },

  deleteCategory: async (id) => {
    const response = await apiClient.delete(`/category/${id}`)
    return response.data
  },

  getUploadLogs: async (params) => {
    const uploadParams = { ...params, type: 'category' }
    const response = await apiClient.get('/admin/upload-logs', { params: uploadParams })
    return response.data
  },

  getUploadLogDetails: async (id) => {
    const response = await apiClient.get(`/admin/upload-logs/${id}`)
    return response.data
  },
}

// Division Services
export const divisionService = {
  importDivisions: async (data) => {
    const formData = new FormData()
    formData.append('file', data.file)
    if (data.batchSize) formData.append('batchSize', data.batchSize)
    const response = await apiClient.post('/division/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  getDivisions: async (params) => {
    const response = await apiClient.get('/division', { params })
    return response.data
  },

  updateDivision: async (id, data) => {
    const response = await apiClient.put(`/division/${id}`, data)
    return response.data
  },

  deleteDivision: async (id) => {
    const response = await apiClient.delete(`/division/${id}`)
    return response.data
  },

  importDivisionAssignment: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post(
      '/admin/admin-products/division-assignment/import',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data
  },

  downloadAssignmentSample: async () => {
    const response = await apiClient.get('/admin/admin-products/division-assignment/sample', {
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const a = document.createElement('a')
    a.href = url
    a.download = 'division-assignment-sample.csv'
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  },

  getUploadLogs: async (params) => {
    const response = await apiClient.get('/admin/upload-logs', {
      params: { ...params, type: 'division,division_assignment' },
    })
    return response.data
  },

  getUploadLogDetails: async (id) => {
    const response = await apiClient.get(`/admin/upload-logs/${id}`)
    return response.data
  },
}

// Delivery Time Services
export const deliveryTimeService = {
  getDeliveryTimes: async (params) => {
    const response = await apiClient.get('/delivery-time', { params })
    return response.data
  },

  updateDeliveryTime: async (id, data) => {
    const response = await apiClient.put(`/delivery-time/${id}`, data)
    return response.data
  },

  deleteDeliveryTime: async (id) => {
    const response = await apiClient.delete(`/delivery-time/${id}`)
    return response.data
  },

  importDeliveryTimes: async (data) => {
    const formData = new FormData()
    formData.append('file', data.file)
    const response = await apiClient.post('/delivery-time/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  downloadMissingCsv: async () => {
    const response = await apiClient.get('/delivery-time/missing-csv', { responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const a = document.createElement('a')
    a.href = url
    a.download = 'missing_delivery_times.csv'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  },

  downloadBulkDeleteCsv: async () => {
    const response = await apiClient.get('/delivery-time/bulk-delete-csv', {
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const a = document.createElement('a')
    a.href = url
    a.download = 'delivery_times_bulk_delete.csv'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  },

  bulkDeleteFromCsv: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post('/delivery-time/bulk-delete', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  bulkDeleteByIds: async (ids) => {
    const response = await apiClient.post('/delivery-time/bulk-delete/ids', { ids })
    return response.data
  },

  bulkDeleteByOrganization: async (orgId) => {
    const response = await apiClient.delete(
      `/delivery-time/bulk-delete/organization/${orgId}`,
    )
    return response.data
  },

  bulkDeleteByLocation: async (locationId) => {
    const response = await apiClient.delete(
      `/delivery-time/bulk-delete/location/${locationId}`,
    )
    return response.data
  },

  bulkDeleteByOrgAndLocation: async (orgId, locationId) => {
    const response = await apiClient.delete(
      `/delivery-time/bulk-delete/organization/${orgId}/location/${locationId}`,
    )
    return response.data
  },

  getUploadLogs: async (params) => {
    const uploadParams = { ...params, type: 'delivery_time' }
    const response = await apiClient.get('/admin/upload-logs', { params: uploadParams })
    return response.data
  },

  getUploadLogDetails: async (id) => {
    const response = await apiClient.get(`/admin/upload-logs/${id}`)
    return response.data
  },
}

// App Release Services (in-app update)
export const appReleaseService = {
  list: async (params) => {
    const response = await apiClient.get('/admin/app-releases', { params })
    return response.data
  },

  getById: async (id) => {
    const response = await apiClient.get(`/admin/app-releases/${id}`)
    return response.data
  },

  getDownloadUrl: async (id) => {
    const response = await apiClient.get(`/admin/app-releases/${id}/download-url`)
    return response.data
  },

  getLatestDownloadUrl: async () => {
    const response = await apiClient.get('/admin/app-releases/latest/download-url')
    return response.data
  },

  getVersionBounds: async () => {
    const response = await apiClient.get('/admin/app-releases/version-bounds')
    return response.data
  },

  create: async ({ file, version_code, version_name, update_type, changelog }, onUploadProgress) => {
    const presign = await apiClient.post('/admin/app-releases/presign-upload', {
      version_code,
      file_size_bytes: file.size,
      original_filename: file.name,
    })

    const { upload_url: uploadUrl, s3_key: s3Key } = presign.data
    await uploadApkToPresignedUrl(file, uploadUrl, onUploadProgress)

    const sha256_checksum = await sha256HexFromFile(file)
    const response = await apiClient.post('/admin/app-releases/complete-upload', {
      version_code,
      version_name,
      update_type,
      changelog: changelog ?? '',
      s3_key: s3Key,
      sha256_checksum,
      file_size_bytes: file.size,
    })
    return response.data
  },

  update: async (id, data) => {
    const response = await apiClient.patch(`/admin/app-releases/${id}`, data)
    return response.data
  },

  activate: async (id) => {
    const response = await apiClient.post(`/admin/app-releases/${id}/activate`)
    return response.data
  },

  deactivate: async (id) => {
    const response = await apiClient.post(`/admin/app-releases/${id}/deactivate`)
    return response.data
  },

  remove: async (id) => {
    const response = await apiClient.delete(`/admin/app-releases/${id}`)
    return response.data
  },
}

// Inventory Sync Approvals (admin)
export const inventorySyncAdminService = {
  list: async (params) => {
    const response = await apiClient.get('/admin/inventory-sync', { params })
    return response.data
  },
  getDetail: async (syncId) => {
    const response = await apiClient.get(`/admin/inventory-sync/${syncId}`)
    return response.data
  },
  exportCsv: async (syncId) => {
    const response = await apiClient.get(
      `/admin/inventory-sync/${syncId}/export`,
      { responseType: 'blob' },
    )
    return response
  },
  approve: async (syncId) => {
    const response = await apiClient.post(`/admin/inventory-sync/${syncId}/approve`)
    return response.data
  },
  reject: async (syncId, reason) => {
    const response = await apiClient.post(
      `/admin/inventory-sync/${syncId}/reject`,
      reason ? { reason } : {},
    )
    return response.data
  },
  getRevertPreflight: async (syncId) => {
    const response = await apiClient.get(
      `/admin/inventory-sync/${syncId}/revert-preflight`,
    )
    return response.data
  },
  revert: async (syncId, reason) => {
    const response = await apiClient.post(
      `/admin/inventory-sync/${syncId}/revert`,
      reason ? { reason } : {},
    )
    return response.data
  },
  delete: async (syncId, reason) => {
    const response = await apiClient.delete(`/admin/inventory-sync/${syncId}`, {
      data: reason ? { reason } : {},
    })
    return response.data
  },
  getAuditLog: async (syncId) => {
    const response = await apiClient.get(`/admin/inventory-sync/${syncId}/audit`)
    return response.data
  },
  importLineItemsCsv: async (syncId, file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post(
      `/admin/inventory-sync/${syncId}/items/import`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
    return response.data
  },
  previewLinkProductsCsv: async (syncId, file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post(
      `/admin/inventory-sync/${syncId}/new-products/import-link-csv/preview`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
    return response.data
  },
  confirmLinkProductsCsv: async (syncId, items) => {
    const response = await apiClient.post(
      `/admin/inventory-sync/${syncId}/new-products/import-link-csv/confirm`,
      { items },
    )
    return response.data
  },
  updateLineItem: async (syncId, lineItemId, payload) => {
    const response = await apiClient.patch(
      `/admin/inventory-sync/${syncId}/items/${lineItemId}`,
      payload,
    )
    return response.data
  },
  approveNewProduct: async (syncId, newProductId) => {
    const response = await apiClient.post(
      `/admin/inventory-sync/${syncId}/new-products/${newProductId}/approve`,
    )
    return response.data
  },
  linkNewProduct: async (syncId, newProductId, adminProductId) => {
    const response = await apiClient.post(
      `/admin/inventory-sync/${syncId}/new-products/${newProductId}/link`,
      { admin_product_id: adminProductId },
    )
    return response.data
  },
  rejectNewProduct: async (syncId, newProductId, reason) => {
    const response = await apiClient.post(
      `/admin/inventory-sync/${syncId}/new-products/${newProductId}/reject`,
      reason ? { reason } : {},
    )
    return response.data
  },
  approveLineItem: async (syncId, lineItemId) => {
    const response = await apiClient.post(
      `/admin/inventory-sync/${syncId}/items/${lineItemId}/approve`,
    )
    return response.data
  },
  rejectLineItem: async (syncId, lineItemId, reason) => {
    const response = await apiClient.post(
      `/admin/inventory-sync/${syncId}/items/${lineItemId}/reject`,
      reason ? { reason } : {},
    )
    return response.data
  },
  bulkApproveLineItems: async (syncId, lineItemIds) => {
    const response = await apiClient.post(
      `/admin/inventory-sync/${syncId}/items/bulk/approve`,
      { line_item_ids: lineItemIds },
    )
    return response.data
  },
  bulkRejectLineItems: async (syncId, lineItemIds, reason) => {
    const response = await apiClient.post(
      `/admin/inventory-sync/${syncId}/items/bulk/reject`,
      { line_item_ids: lineItemIds, ...(reason ? { reason } : {}) },
    )
    return response.data
  },
  bulkUpdateLineItemsNonShwapno: async (syncId, lineItemIds, isNonShwapno) => {
    const response = await apiClient.post(
      `/admin/inventory-sync/${syncId}/items/bulk/is-non-shwapno`,
      { line_item_ids: lineItemIds, is_non_shwapno: !!isNonShwapno },
    )
    return response.data
  },

  createForOrg: async (orgId, { name, notes }) => {
    const response = await apiClient.post(`/admin/inventory-sync/org/${orgId}`, {
      name,
      ...(notes ? { notes } : {}),
    })
    return response.data
  },

  checkName: async (orgId, name) => {
    const response = await apiClient.get(
      `/admin/inventory-sync/org/${orgId}/check-name`,
      { params: { name } },
    )
    return response.data
  },

  submit: async (syncId, payload) => {
    const response = await apiClient.post(
      `/admin/inventory-sync/${syncId}/submit`,
      payload,
    )
    return response.data
  },

  importCreationCsv: async (orgId, file, { name, notes }) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('name', name)
    if (notes) formData.append('notes', notes)
    const response = await apiClient.post(
      `/admin/inventory-sync/org/${orgId}/import-csv`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data
  },

  downloadCreationCsvTemplate: async () => {
    return apiClient.get('/admin/inventory-sync/import-csv/template', {
      responseType: 'blob',
    })
  },

  getProducts: async (params = {}) => {
    const response = await apiClient.get('/admin/inventory-sync/products', {
      params,
    })
    return response.data
  },

  getUploadLogs: async (params = {}) => {
    const response = await apiClient.get('/admin/upload-logs', {
      params: { ...params, type: 'inventory_sync' },
    })
    return response.data
  },
}

export const purchaseOrderImportService = {
  uploadCsv: async ({ file, orgId }) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('orgId', String(orgId))
    const response = await apiClient.post('/admin/purchase-order-imports', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  listSessions: async ({ orgId, page = 1, limit = 10, status }) => {
    const response = await apiClient.get('/admin/purchase-order-imports', {
      params: { orgId, page, limit, status },
    })
    return response.data
  },

  getSession: async (sessionId) => {
    const response = await apiClient.get(`/admin/purchase-order-imports/${sessionId}`)
    return response.data
  },

  getLines: async (sessionId, { page = 1, limit = 20, status, search } = {}) => {
    const response = await apiClient.get(
      `/admin/purchase-order-imports/${sessionId}/lines`,
      { params: { page, limit, status, search } },
    )
    return response.data
  },

  getAlternatives: async (sessionId, lineId) => {
    const response = await apiClient.get(
      `/admin/purchase-order-imports/${sessionId}/lines/${lineId}/alternatives`,
    )
    return response.data
  },

  patchLine: async (sessionId, lineId, body) => {
    const response = await apiClient.patch(
      `/admin/purchase-order-imports/${sessionId}/lines/${lineId}`,
      body,
    )
    return response.data
  },

  reResolveLine: async (sessionId, lineId) => {
    const response = await apiClient.post(
      `/admin/purchase-order-imports/${sessionId}/lines/${lineId}/re-resolve`,
    )
    return response.data
  },

  remediateLine: async (sessionId, lineId, body) => {
    const response = await apiClient.post(
      `/admin/purchase-order-imports/${sessionId}/lines/${lineId}/remediate`,
      body,
    )
    return response.data
  },

  submit: async (sessionId) => {
    const response = await apiClient.post(
      `/admin/purchase-order-imports/${sessionId}/submit`,
    )
    return response.data
  },

  exportErrors: async (sessionId) => {
    const response = await apiClient.get(
      `/admin/purchase-order-imports/${sessionId}/errors/export`,
      { responseType: 'blob' },
    )
    return response.data
  },
}

export const purchasableStatusAdminService = {
  run: async () => {
    const response = await apiClient.post('/admin/purchasable-status/run')
    return response.data
  },
  listRuns: async (params) => {
    const response = await apiClient.get('/admin/purchasable-status/runs', { params })
    return response.data
  },
  getLatestRun: async () => {
    const response = await apiClient.get('/admin/purchasable-status/runs/latest')
    return response.data
  },
  getApiData: async (id) => {
    const response = await apiClient.get(`/admin/purchasable-status/runs/${id}/api-data`)
    return response.data
  },
}

export const stockSyncAdminService = {
  runStockSync: async (body) => {
    const response = await apiClient.post('/admin/stock-sync/run', body || {})
    return response.data
  },
  listRuns: async (params) => {
    const response = await apiClient.get('/admin/stock-sync/runs', { params })
    return response.data
  },
  getLatestRun: async () => {
    const response = await apiClient.get('/admin/stock-sync/runs/latest')
    return response.data
  },
  listItems: async (id, params) => {
    const response = await apiClient.get(`/admin/stock-sync/runs/${id}/items`, {
      params,
    })
    return response.data
  },
  listFailedLocations: async (id, params) => {
    const response = await apiClient.get(
      `/admin/stock-sync/runs/${id}/failed-locations`,
      { params },
    )
    return response.data
  },
  listProcessedLocations: async (id, params) => {
    const response = await apiClient.get(
      `/admin/stock-sync/runs/${id}/processed-locations`,
      { params },
    )
    return response.data
  },
  downloadProcessedLocationsCsv: async (id) => {
    const response = await apiClient.get(
      `/admin/stock-sync/runs/${id}/processed-locations/csv`,
      { responseType: 'blob' },
    )
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const a = document.createElement('a')
    a.href = url
    a.download = `stock-sync-${id}-processed-locations.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  },
  downloadFailedLocationsCsv: async (id) => {
    const response = await apiClient.get(
      `/admin/stock-sync/runs/${id}/failed-locations/csv`,
      { responseType: 'blob' },
    )
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const a = document.createElement('a')
    a.href = url
    a.download = `stock-sync-${id}-failed-locations.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  },
  getPriceChanges: async (id) => {
    const response = await apiClient.get(`/admin/stock-sync/runs/${id}/price-changes`)
    return response.data
  },
  getApiData: async (id) => {
    const response = await apiClient.get(`/admin/stock-sync/runs/${id}/api-data`)
    return response.data
  },
  stopRun: async (id) => {
    const response = await apiClient.post(`/admin/stock-sync/runs/${id}/stop`)
    return response.data
  },
}

export const promotionSyncAdminService = {
  run: async () => {
    const response = await apiClient.post('/admin/promotion-sync/run')
    return response.data
  },
  listRuns: async (params) => {
    const response = await apiClient.get('/admin/promotion-sync/runs', { params })
    return response.data
  },
  getLatestRun: async () => {
    const response = await apiClient.get('/admin/promotion-sync/runs/latest')
    return response.data
  },
  getSchedule: async () => {
    const response = await apiClient.get('/admin/promotion-sync/schedule')
    return response.data
  },
  listFailedLocations: async (id, params) => {
    const response = await apiClient.get(
      `/admin/promotion-sync/runs/${id}/failed-locations`,
      { params },
    )
    return response.data
  },
  downloadFailedLocationsCsv: async (id) => {
    const response = await apiClient.get(
      `/admin/promotion-sync/runs/${id}/failed-locations/csv`,
      { responseType: 'blob' },
    )
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const a = document.createElement('a')
    a.href = url
    a.download = `promotion-sync-${id}-failed-locations.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  },
  getApiData: async (id) => {
    const response = await apiClient.get(`/admin/promotion-sync/runs/${id}/api-data`)
    return response.data
  },
  stopRun: async (id) => {
    const response = await apiClient.post(`/admin/promotion-sync/runs/${id}/stop`)
    return response.data
  },
}

/** Administration → Customer Support */
export const customerSupportAdminService = {
  listActions: async (params) => {
    const response = await apiClient.get('/admin/customer-support/actions', {
      params,
    })
    return response.data
  },
  getAction: async (actionId) => {
    const response = await apiClient.get(
      `/admin/customer-support/actions/${actionId}`,
    )
    return response.data
  },
  /** Proxied artifact body from the admin API (not a presigned S3 URL). */
  downloadArtifact: async (artifactId) => {
    const response = await apiClient.get(
      `/admin/customer-support/artifacts/${artifactId}/download`,
    )
    return response.data
  },
  reportsMirror: async (params) => {
    const response = await apiClient.get(
      '/admin/customer-support/reports-mirror',
      { params },
    )
    return response.data
  },
  reportsInvoices: async (params) => {
    const response = await apiClient.get(
      '/admin/customer-support/reports-mirror/invoices',
      { params },
    )
    return response.data
  },
  reportsProfitDetail: async (params) => {
    const response = await apiClient.get(
      '/admin/customer-support/reports-mirror/profit-detail',
      { params },
    )
    return response.data
  },
  reportsProducts: async (params) => {
    const response = await apiClient.get(
      '/admin/customer-support/reports-mirror/products',
      { params },
    )
    return response.data
  },
  reportsPromotions: async (params) => {
    const response = await apiClient.get(
      '/admin/customer-support/reports-mirror/promotions',
      { params },
    )
    return response.data
  },
  reportsReturns: async (params) => {
    const response = await apiClient.get(
      '/admin/customer-support/reports-mirror/returns',
      { params },
    )
    return response.data
  },
  priceRca: async (params) => {
    const response = await apiClient.get('/admin/customer-support/price-rca', {
      params,
    })
    return response.data
  },
  getQueryCatalog: async () => {
    const response = await apiClient.get(
      '/admin/customer-support/query/catalog',
    )
    return response.data
  },
  runQuery: async (body) => {
    const response = await apiClient.post(
      '/admin/customer-support/query/run',
      body,
    )
    return response.data
  },
  getExportCatalog: async () => {
    const response = await apiClient.get(
      '/admin/customer-support/export/catalog',
    )
    return response.data
  },
  exportAudit: async (body) => {
    return apiClient.post('/admin/customer-support/export/audit', body, {
      responseType: 'blob',
    })
  },
  exportReports: async (body) => {
    return apiClient.post('/admin/customer-support/export/reports', body, {
      responseType: 'blob',
    })
  },
  exportPriceRca: async (body) => {
    return apiClient.post('/admin/customer-support/export/price-rca', body, {
      responseType: 'blob',
    })
  },
  exportQuery: async (body) => {
    return apiClient.post('/admin/customer-support/export/query', body, {
      responseType: 'blob',
    })
  },
  wipeDryRun: async (body) => {
    const response = await apiClient.post(
      '/admin/customer-support/wipe/dry-run',
      body,
    )
    return response.data
  },
  wipeSubmit: async (actionId) => {
    const response = await apiClient.post(
      `/admin/customer-support/wipe/${actionId}/submit`,
    )
    return response.data
  },
  wipeApprove: async (actionId) => {
    const response = await apiClient.post(
      `/admin/customer-support/wipe/${actionId}/approve`,
    )
    return response.data
  },
  wipeReject: async (actionId, body) => {
    const response = await apiClient.post(
      `/admin/customer-support/wipe/${actionId}/reject`,
      body || {},
    )
    return response.data
  },
  wipeExecute: async (actionId) => {
    const response = await apiClient.post(
      `/admin/customer-support/wipe/${actionId}/execute`,
    )
    return response.data
  },
  adjustDryRun: async (body) => {
    const response = await apiClient.post(
      '/admin/customer-support/adjust/dry-run',
      body,
    )
    return response.data
  },
  adjustBulkDryRun: async (body) => {
    const response = await apiClient.post(
      '/admin/customer-support/adjust/bulk/dry-run',
      body,
    )
    return response.data
  },
  adjustApply: async (actionId) => {
    const response = await apiClient.post(
      `/admin/customer-support/adjust/${actionId}/apply`,
    )
    return response.data
  },
  downloadCustomerImportSample: async () => {
    const response = await apiClient.get(
      '/admin/customer-support/customers/import/sample',
      { responseType: 'blob' },
    )
    return response.data
  },
  customerImportDryRun: async ({ file, orgId, reason }) => {
    const form = new FormData()
    form.append('file', file)
    form.append('orgId', String(orgId))
    form.append('reason', reason)
    const response = await apiClient.post(
      '/admin/customer-support/customers/import/dry-run',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data
  },
  customerImportApply: async (actionId) => {
    const response = await apiClient.post(
      `/admin/customer-support/customers/import/${actionId}/apply`,
    )
    return response.data
  },
}
